import { useState } from 'react'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'

export interface AddConcertFormData {
  date_text: string
  year: number
  date_iso?: string
  venue_name?: string
  ensemble_name?: string
  notes?: string
}

interface AddConcertDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: AddConcertFormData) => Promise<void>
  isPending: boolean
}

export function AddConcertDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: AddConcertDialogProps) {
  const [dateText, setDateText] = useState('')
  const [year, setYear] = useState('')
  const [dateIso, setDateIso] = useState('')
  const [venueName, setVenueName] = useState('')
  const [ensembleName, setEnsembleName] = useState('')
  const [notes, setNotes] = useState('')

  const resetForm = () => {
    setDateText('')
    setYear('')
    setDateIso('')
    setVenueName('')
    setEnsembleName('')
    setNotes('')
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm()
    onOpenChange(nextOpen)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const yearNum = parseInt(year, 10)
    if (!dateText.trim() || isNaN(yearNum)) return

    await onSubmit({
      date_text: dateText.trim(),
      year: yearNum,
      date_iso: dateIso.trim() || undefined,
      venue_name: venueName.trim() || undefined,
      ensemble_name: ensembleName.trim() || undefined,
      notes: notes.trim() || undefined,
    })
    resetForm()
  }

  const isValid = dateText.trim() && year && !isNaN(parseInt(year, 10))

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Concert</DialogTitle>
          <DialogDescription>
            Create a new concert record. Date and year are required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="add-concert-date" className="text-sm font-medium">
                Date text *
              </label>
              <Input
                id="add-concert-date"
                value={dateText}
                onChange={(e) => setDateText(e.target.value)}
                placeholder="e.g. 18 March 1995"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="add-concert-year" className="text-sm font-medium">
                Year *
              </label>
              <Input
                id="add-concert-year"
                type="number"
                min={1900}
                max={2100}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                placeholder="e.g. 1995"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="add-concert-iso" className="text-sm font-medium">
                ISO date
              </label>
              <Input
                id="add-concert-iso"
                type="date"
                value={dateIso}
                onChange={(e) => setDateIso(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="add-concert-venue" className="text-sm font-medium">
                Venue
              </label>
              <Input
                id="add-concert-venue"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder="e.g. St Martin's Church"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="add-concert-ensemble" className="text-sm font-medium">
                Ensemble
              </label>
              <Input
                id="add-concert-ensemble"
                value={ensembleName}
                onChange={(e) => setEnsembleName(e.target.value)}
                placeholder="e.g. Brent Symphony Orchestra"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="add-concert-notes" className="text-sm font-medium">
              Notes
            </label>
            <Input
              id="add-concert-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isPending}>
              {isPending ? 'Creating...' : 'Create Concert'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
